// =====================================================
// DASHBOARD STATS — Tableau de Romain (RUN 6, TOUTE FIN de page)
// =====================================================
// Section « Tableau de Romain (acquisition externe) » du cadrage 07/07 : un
// tableau MENSUEL auto-généré, une ligne par mois, avec comparaison N-1 mois par
// mois. Lucile s'en sert pour ses points. Fonctions pures, testables en vitest,
// sans dépendance React.
//
// ⚠️ FLAG DESERTOURS — FORMAT EXACT NON CONFIRMÉ
//   Les 4 lignes/libellés EXACTS imposés par Romain ne sont pas encore reçus de
//   Paul (« Paul enverra les lignes exactes »). On implémente la STRUCTURE sur
//   les métriques CONNUES du cadrage, en best-effort mapping contre le schéma
//   réel, et on rend la config des colonnes FACILEMENT AJUSTABLE : les 3 métriques
//   ci-dessous (`ROMAIN_METRICS`) sont la SEULE source à éditer quand le format
//   final arrive (ajouter/renommer/redéfinir une métrique = 1 entrée de tableau).
//
// ── MÉTRIQUES (cadrage 07/07, best-effort vs schéma réel) ────────────────────
//   1. Bulletins de préinscription complétés / mois  → tous les BPI générés (flux, 22/07)
//   2. Préinscrits rentrés / mois                     → crews atteignant PI (flux)
//   3. Préinscrits en attente (en lice)               → PI pas encore ID (stock à fin de mois)
//   La 4e « donnée » de Romain = la comparaison N-1 (rendue mois par mois pour
//   CHAQUE métrique), pas une métrique séparée.
//
// ── N-1 (convention codebase) ────────────────────────────────────────────────
//   Comparaison BRUTE par édition sur la MÊME fenêtre calendaire (comme flux.ts /
//   origin.ts / conversion.ts) : N-1 d'un mois M = même métrique calculée sur
//   l'édition N-1 pour le MÊME mois M. L'ancrage année-sur-année (décalage +12
//   mois « éditions ~mêmes dates chaque année ») est DIFFÉRÉ, même flag que
//   periods.ts : sans séparation temporelle réelle du seed, un décalage
//   calendaire exclurait à tort les lignes N-1.

import { countBpiEntres } from './flux';
import { isIdCrew, isPiCrew } from './nomenclature';
import { inWindow, shiftWindowYears } from './periods';
import type { DateWindow, EditionRef, RawRows } from './types';

/** Nature d'une métrique : `flow` = entrées du mois ; `stock` = état à fin de mois. */
export type RomainMetricKind = 'flow' | 'stock';

/**
 * Définition d'une colonne/métrique du tableau de Romain. **Source unique à éditer**
 * quand le format exact arrive de Desertours (§ FLAG ci-dessus).
 */
export interface RomainMetricDef {
  /** Clé stable (indexe les cellules d'une ligne). */
  key: string;
  /** Libellé de colonne affiché (best-effort vs libellé final Romain). */
  label: string;
  /** Sous-titre / définition (survol, note). */
  description: string;
  /** `flow` (entrées du mois) ou `stock` (état cumulé à fin de mois). */
  kind: RomainMetricKind;
  /** true tant que le libellé/mapping exact reste à confirmer avec Desertours. */
  flagged: boolean;
  /**
   * Calcule la valeur pour une édition sur une fenêtre de mois.
   * - `flow` : compte les entrées DANS la fenêtre [start, end] du mois.
   * - `stock` : compte l'état À `w.end` (cumul jusqu'à fin de mois).
   */
  compute: (rows: RawRows, editionId: string, w: DateWindow) => number;
}

/** ⏱️ Nombre de crews PI (pas encore ID) d'une édition existant à `asOf`. */
function countPiEnAttenteAsOf(rows: RawRows, editionId: string, asOf: Date): number {
  const t = asOf.getTime();
  return rows.crews.filter(
    (c) =>
      c.product_id === editionId &&
      isPiCrew(c) &&
      !isIdCrew(c) &&
      new Date(c.created_at).getTime() <= t
  ).length;
}

/** ➕ crews atteignant la pré-inscription (PI), entrés (created_at) dans la fenêtre. */
function countPiRentres(rows: RawRows, editionId: string, w: DateWindow): number {
  return rows.crews.filter(
    (c) => c.product_id === editionId && isPiCrew(c) && inWindow(c.created_at, w)
  ).length;
}

/**
 * ⚙️ MÉTRIQUES DU TABLEAU DE ROMAIN — **la config à éditer** quand Paul envoie
 * le format exact. Ajouter/renommer/redéfinir une métrique = modifier CE tableau ;
 * l'UI et l'export CSV s'adaptent automatiquement (rendu générique par colonne).
 */
export const ROMAIN_METRICS: RomainMetricDef[] = [
  {
    key: 'bpi_completes',
    label: 'Bulletins de préinscription complétés',
    description: 'Tous les BPI générés (préinscriptions, payés ou non) entrés dans le mois.',
    kind: 'flow',
    flagged: true,
    compute: (rows, eid, w) => countBpiEntres(rows, eid, w),
  },
  {
    key: 'pi_rentres',
    label: 'Préinscrits rentrés',
    description: 'Équipages ayant atteint la pré-inscription, entrés dans le mois.',
    kind: 'flow',
    flagged: true,
    compute: (rows, eid, w) => countPiRentres(rows, eid, w),
  },
  {
    key: 'pi_en_attente',
    label: 'Préinscrits en attente (en lice)',
    description:
      'Pré-inscrits pas encore inscrits définitifs, à la fin du mois (stock « en lice »).',
    kind: 'stock',
    flagged: true,
    compute: (rows, eid, w) => countPiEnAttenteAsOf(rows, eid, w.end),
  },
];

/** Métadonnées publiques d'une métrique (sans la fonction `compute`). */
export type RomainMetricMeta = Omit<RomainMetricDef, 'compute'>;

/** Une cellule = valeur N + valeur N-1 (null si N-1 indisponible). */
export interface RomainCell {
  value: number;
  /** Valeur N-1 (même mois, édition N-1) ; null si aucune édition N-1 appariée. */
  prevValue: number | null;
}

/** Une ligne du tableau = un mois calendaire, cellules indexées par clé de métrique. */
export interface RomainRow {
  /** Borne basse ISO du mois (tri / debug). */
  monthStart: string;
  /** Libellé FR affiché (ex. « déc. 2026 »). */
  label: string;
  /** Cellules par clé de métrique (`ROMAIN_METRICS[i].key`). */
  cells: Record<string, RomainCell>;
}

export interface RomainData {
  /** Métriques (colonnes), dans l'ordre — méta sans la fonction compute. */
  metrics: RomainMetricMeta[];
  /** Lignes mensuelles, du plus ancien au plus récent. */
  rows: RomainRow[];
  /** true si une édition N-1 est appariée (colonnes N-1 pertinentes). */
  hasN1: boolean;
  /** true si aucun mois n'a de donnée (édition vide). */
  isEmpty: boolean;
  /** Réserves à confirmer avec Desertours (nomenclature Romain). */
  flags: string[];
}

export interface RomainParams {
  rows: RawRows;
  edition: EditionRef;
  now: Date;
  /** Plafond de mois affichés (défaut 18) — borne le tableau, garde le plus récent. */
  maxMonths?: number;
}

const MONTHS_FR = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];

/** 1er du mois de `d` à 00:00 local (copie, ne mute pas). */
function startOfMonth(d: Date): Date {
  const c = new Date(d.getTime());
  c.setDate(1);
  c.setHours(0, 0, 0, 0);
  return c;
}

/** Décale de `n` mois (n peut être négatif). */
function addMonths(d: Date, n: number): Date {
  const c = new Date(d.getTime());
  c.setMonth(c.getMonth() + n);
  return c;
}

/** Fenêtre inclusive [1er du mois, dernière ms du mois]. */
function monthWindow(monthStart: Date): DateWindow {
  return { start: monthStart, end: new Date(addMonths(monthStart, 1).getTime() - 1) };
}

function monthLabel(d: Date): string {
  return `${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

/** Plus ancien created_at pertinent (BPI payés + crews + soumissions) d'une édition. */
function earliestEditionDate(rows: RawRows, editionId: string): Date | null {
  let min = Infinity;
  const consider = (iso: string | null | undefined) => {
    if (!iso) return;
    const t = new Date(iso).getTime();
    if (Number.isFinite(t) && t < min) min = t;
  };
  rows.crews.forEach((c) => c.product_id === editionId && consider(c.created_at));
  rows.bpiPayments.forEach(
    (p) => p.product_id === editionId && p.status === 'paid' && consider(p.created_at)
  );
  (rows.bpiFormSubmissions ?? []).forEach(
    (f) => f.product_id === editionId && consider(f.created_at)
  );
  return Number.isFinite(min) ? new Date(min) : null;
}

export const ROMAIN_FLAGS = [
  'Format exact du tableau de Romain NON confirmé (« Paul enverra les lignes exactes ») — structure best-effort sur 3 métriques du cadrage, config ajustable (ROMAIN_METRICS).',
  "« Préinscrits en attente (en lice) » = stock PI non-ID à fin de mois ; faute d'horodatage de transition de statut en base, le stock des mois passés est reconstruit depuis le STATUT COURANT (approximation).",
  "N-1 = le même mois décalé d'un an (année-1) sur l'édition N-1 (aligné date-picker RETOUCHE 1).",
] as const;

/**
 * Tableau de Romain : une ligne par mois de l'édition N (du 1er mois de données
 * jusqu'au mois courant, borné à `maxMonths`), chaque métrique en valeur N + N-1.
 */
export function computeRomain({ rows, edition, now, maxMonths = 18 }: RomainParams): RomainData {
  const prevId = edition.prevEditionId;
  const hasN1 = !!prevId;

  const nowMonth = startOfMonth(now);
  const earliest = earliestEditionDate(rows, edition.id);
  const firstMonth = earliest ? startOfMonth(earliest) : nowMonth;

  // Liste des mois [firstMonth … nowMonth], bornée à maxMonths (garde le récent).
  const months: Date[] = [];
  let cursor = firstMonth;
  while (cursor.getTime() <= nowMonth.getTime()) {
    months.push(cursor);
    cursor = addMonths(cursor, 1);
  }
  if (months.length === 0) months.push(nowMonth);
  const bounded = months.length > maxMonths ? months.slice(months.length - maxMonths) : months;

  const rowsOut: RomainRow[] = bounded.map((monthStart) => {
    const w = monthWindow(monthStart);
    // RETOUCHE 1 : N-1 = le MÊME mois décalé d'un an (année-1) sur l'édition N-1,
    // aligné sur la convention date-picker (plus la « même fenêtre brute »).
    const wPrev = shiftWindowYears(w, -1);
    const cells: Record<string, RomainCell> = {};
    for (const m of ROMAIN_METRICS) {
      cells[m.key] = {
        value: m.compute(rows, edition.id, w),
        prevValue: prevId ? m.compute(rows, prevId, wPrev) : null,
      };
    }
    return { monthStart: monthStart.toISOString(), label: monthLabel(monthStart), cells };
  });

  // Vide = toutes les cellules N à 0 sur tous les mois.
  const isEmpty = rowsOut.every((r) => ROMAIN_METRICS.every((m) => r.cells[m.key].value === 0));

  return {
    metrics: ROMAIN_METRICS.map(({ compute: _compute, ...meta }) => meta),
    rows: rowsOut,
    hasN1,
    isEmpty,
    flags: [...ROMAIN_FLAGS],
  };
}
